import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BehaviorSubject, filter, firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { GeneralParameterEnums } from 'src/app/enum/general-parameter-enums';
import { ConceptInvoicingModel } from 'src/app/models/invoicing/concept-invoicing.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { GeneralParameterService } from 'src/app/services/management/general-parameter.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { AssignFamiliesComponent } from '../../../management/accounting-model/assign-families/assign-families.component';
import { ConceptInvoicingEnums, ConceptInvoicingPriceOfEnums, ConceptInvoicingTypeEnums, PurchaseConceptEnums } from 'src/app/enum/concept-invoicing-enums';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';

@Component({
  selector: 'app-register-concept-invoicing',
  templateUrl: './register-concept-invoicing.component.html',
  styleUrls: ['./register-concept-invoicing.component.scss'],
  standalone: false
})
export class RegisterConceptInvoicingComponent implements OnInit {

  @ViewChild('assignFamiliesModal') assignFamiliesModalComponent?: CustomModalComponent | null = null;

  companyId: number = 0;
  conceptInvoicingContract: ConceptInvoicingModel = new ConceptInvoicingModel();
  disabledForm: boolean = false;
  formConceptInvoicing: FormGroup = new FormGroup({});
  inputsForAssignFamilies$ = new BehaviorSubject<any>({
    conceptInvoicingId: 0,
  });
  conceptInvoicingId: number = 0;
  url: string = 'concept-invoicing';
  nameWindow: string = 'Captura Conceptos de Facturación';
  projectName = ProjectName.projectName;
  listConceptInvoicingPriceOfs: any[] = [];
  listConceptInvoicingTypes: CustomListDropdown[] = [
    { label: 'Venta', value: '1' },
    { label: 'Compra', value: '2' }
  ];
  conceptInvoicingType: number = 1;
  permissionUpdatable: boolean = false;
  subscription: Subscription;
  swDispersedArticle: boolean = false;
  componentAssignFamilies = AssignFamiliesComponent;

  constructor(private conceptInvoicingService: ConceptInvoicingService,
    private confirmDialogService: ConfirmDialogService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private generalParameterService: GeneralParameterService,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
    this.validateForm(new ConceptInvoicingModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe(async (inputs: any) => {
      if (inputs?.type === 'reload') return;
      this.conceptInvoicingContract = inputs.conceptInvoicingModel;
      this.conceptInvoicingType = inputs.conceptInvoicingType || this.conceptInvoicingContract.conceptInvoicingType || 1;
      this.conceptInvoicingContract.conceptInvoicingType = this.conceptInvoicingType;

      if (this.conceptInvoicingContract && this.conceptInvoicingContract.conceptInvoicingId != 0) {
        this.conceptInvoicingId = this.conceptInvoicingContract.conceptInvoicingId;
      }
      this.validateForm(this.conceptInvoicingContract);
      await this.getPermission();
      this.handleInitialState();
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  assignFamilies() {
    if (!this.conceptInvoicingContract.swDispersedFamily) {
      this.toastHandlerService.showToast('warning', 'Debe guardar los cambios para poder asignar familias.');
      return;
    }
    this.updateInput(this.conceptInvoicingContract);
  }

  changeCode(code: number) {
    if (code && code > 0) {
      this.getConceptInvoicingByCode(code);
    } else {
      this.createNewRegister();
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear conceptos de facturación.
          Comuníquese con el administrador del sistema.`);
      return;
    }
    this.conceptInvoicingId = 0;
    this.conceptInvoicingContract = new ConceptInvoicingModel();
    this.conceptInvoicingContract.conceptInvoicingType = this.conceptInvoicingType;
    this.validateForm(this.conceptInvoicingContract);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar el concepto de facturación.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.conceptInvoicingId) {
      this.toastHandlerService.showToast('warning', 'Debe crear un concepto de facturación.');
      return;
    }
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar el concepto de facturación?`);

    if (accepted) {
      try {
        if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
          await firstValueFrom(this.conceptInvoicingService.deletePurchaseConcept(this.conceptInvoicingContract.conceptInvoicingId));
        } else {
          await firstValueFrom(this.conceptInvoicingService.deleteConceptInvoicing(this.conceptInvoicingContract.conceptInvoicingId));
        }
        this.createNewRegister();
        this.toastHandlerService.showToast('success', 'El concepto de facturación fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el concepto de facturación.', error);
      }
    }
  }

  async getAllConceptInvoicingPriceOfsForSelect() {
    try {
      const response = await firstValueFrom(this.conceptInvoicingService.getAllConceptInvoicingPriceOfsForSelect());
      this.listConceptInvoicingPriceOfs = response;
    } catch (error) {
      console.error('Error al obtener el concepto de facturación por id.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    const code = this.formConceptInvoicing.get("code")?.value;
    this.handleConceptInvoicingNavigation(buttonClick, code)
  }

  async getConceptInvoicingByCode(code: number) {
    try {
      const response = await firstValueFrom(this.conceptInvoicingService.getConceptInvoicingByCode(code));
      this.conceptInvoicingId = response.conceptInvoicingId;
      this.conceptInvoicingContract = response;
      this.validateForm(this.conceptInvoicingContract);
    } catch (error) {
      console.error('Error al obtener el concepto de facturación por código.', error);
    }
  }

  async getConceptInvoicingById() {
    try {
      const response = await firstValueFrom(this.conceptInvoicingService.getConceptInvoicingById(this.conceptInvoicingId));
      this.conceptInvoicingContract = response;
      this.validateForm(this.conceptInvoicingContract);
    } catch (error) {
      console.error('Error al obtener el concepto de facturación por id.', error);
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const response = await firstValueFrom(this.userAuthenticatedService.branchObservable
        .pipe(filter(branch => branch && branch.branchId !== 0)));
      this.companyId = response.companyId;
    } catch (error) {
      console.error('Error al obtener la sucursal principal del usuario autenticado.', error);
    }
  }

  async getGeneralParameter() {
    if (this.companyId != null && this.companyId != 0) {
      try {
        const response = await firstValueFrom(this.generalParameterService.getGeneralParametersByCompany(this.companyId));
        this.swDispersedArticle = response.find(x => x.generalParameterId == GeneralParameterEnums.activaModelosContables) != null;
      } catch (error) {
        console.error('Error al obtener parámetros generales:', error);
      }
    }
  }

  async getPermission() {
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getGeneralParameter();
    await Promise.all([
      this.getPermissionProfileByUrl(),
      this.getAllConceptInvoicingPriceOfsForSelect(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async handleInitialState() {
    if (this.conceptInvoicingId) {
      await this.getConceptInvoicingById();
    } else {
      this.createNewRegister();
    }
  }

  async handleConceptInvoicingNavigation(buttonClick: any, code: number) {
    let actionMap: Record<string, () => Promise<ConceptInvoicingModel>>;
    if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
      actionMap = {
        first: () => firstValueFrom(this.conceptInvoicingService.getFirstPurchaseConceptByKeys()),
        previus: () => firstValueFrom(this.conceptInvoicingService.getPreviusPurchaseConceptByKeys(code)),
        next: () => firstValueFrom(this.conceptInvoicingService.getNextPurchaseConceptByKeys(code)),
        last: () => firstValueFrom(this.conceptInvoicingService.getLastPurchaseConceptByKeys()),
      };
    } else {
      actionMap = {
        first: () => firstValueFrom(this.conceptInvoicingService.getFirstConceptsInvoicingByKeys()),
        previus: () => firstValueFrom(this.conceptInvoicingService.getPreviusConceptsInvoicingByKeys(code)),
        next: () => firstValueFrom(this.conceptInvoicingService.getNextConceptsInvoicingByKeys(code)),
        last: () => firstValueFrom(this.conceptInvoicingService.getLastConceptsInvoicingByKeys()),
      };
    }
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.conceptInvoicingId = 0;
      this.conceptInvoicingContract = new ConceptInvoicingModel();
      return;
    }
    try {
      const conceptInvoicing = await action();
      this.conceptInvoicingId = conceptInvoicing.conceptInvoicingId;
      this.conceptInvoicingContract = conceptInvoicing;
      this.validateForm(this.conceptInvoicingContract);
    } catch (error) {
      this.conceptInvoicingContract = new ConceptInvoicingModel();
      this.conceptInvoicingContract.conceptInvoicingId = 0;
      this.conceptInvoicingContract.code = code;
      console.error(`Error al buscar el concepto de facturación (${buttonClick.target.id}).`, error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formConceptInvoicing.valid) {
      const modelConceptInvoicing = this.formConceptInvoicing.getRawValue() as ConceptInvoicingModel;
      if (modelConceptInvoicing.alterCode == "") modelConceptInvoicing.alterCode = null;
      if (modelConceptInvoicing.alterCode2 == "") modelConceptInvoicing.alterCode2 = null;

      if (this.conceptInvoicingType !== 1) {
        modelConceptInvoicing.cxcConcept = null;
        modelConceptInvoicing.conceptInvoicingPriceOfId = ConceptInvoicingPriceOfEnums.ninguno;
        modelConceptInvoicing.affectsInventory = null;
        modelConceptInvoicing.promptPayment = null;
      }

      if (this.conceptInvoicingId == 0) {
        try {
          let response;
          if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
            response = await firstValueFrom(this.conceptInvoicingService.createPurchaseConcept(modelConceptInvoicing));
          } else {
            response = await firstValueFrom(this.conceptInvoicingService.createConceptInvoicing(modelConceptInvoicing));
          }
          const typeMessage = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra ? 'Compra' : 'Facturación';
          this.conceptInvoicingId = response.conceptInvoicingId;
          this.conceptInvoicingContract = response;
          this.validateForm(this.conceptInvoicingContract);
          this.toastHandlerService.showToast('success', `El concepto de ${typeMessage} fue creado exitosamente.`);
        } catch (error) {
          console.error('Error al crear el concepto de facturación.', error);
        }
      } else {
        this.conceptInvoicingContract.alterCode = modelConceptInvoicing.alterCode;
        this.conceptInvoicingContract.alterCode2 = modelConceptInvoicing.alterCode2;
        this.conceptInvoicingContract.name = modelConceptInvoicing.name;
        this.conceptInvoicingContract.cxcConcept = modelConceptInvoicing.cxcConcept;
        this.conceptInvoicingContract.affectsInventory = modelConceptInvoicing.affectsInventory;
        this.conceptInvoicingContract.promptPayment = modelConceptInvoicing.promptPayment;
        this.conceptInvoicingContract.conceptInvoicingPriceOfId = modelConceptInvoicing.conceptInvoicingPriceOfId;
        this.conceptInvoicingContract.swDispersedFamily = modelConceptInvoicing.swDispersedFamily;
        try {
          if ((this.conceptInvoicingContract.code == ConceptInvoicingEnums.alquiler || this.conceptInvoicingContract.code == ConceptInvoicingEnums.transporte ||
            this.conceptInvoicingContract.code == ConceptInvoicingEnums.faltantes_reposiciones || this.conceptInvoicingContract.code == ConceptInvoicingEnums.reparaciones ||
            this.conceptInvoicingContract.code == ConceptInvoicingEnums.accesorios || this.conceptInvoicingContract.code == ConceptInvoicingEnums.limpieza ||
            this.conceptInvoicingContract.code == ConceptInvoicingEnums.administracion || this.conceptInvoicingContract.code == ConceptInvoicingEnums.imprevistos ||
            this.conceptInvoicingContract.code == ConceptInvoicingEnums.utilidad) && this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta) {
            const accepted = await this.confirmDialogService.confirm('Se recomienda no modificar los conceptos de facturación con codigo 1-9, ya que son conceptos de facturación que crea el sistema por defecto.¿Realmente desea modificar este concepto de facturación?');
            if (!accepted) return;
          } else {
            if (this.conceptInvoicingContract.code == PurchaseConceptEnums.compras || this.conceptInvoicingContract.code == PurchaseConceptEnums.servicios) {
              const accepted = await this.confirmDialogService.confirm('Se recomienda no modificar los conceptos de compra con codigo 1-2, ya que son conceptos de compra que crea el sistema por defecto.¿Realmente desea modificar este concepto de compra?');
              if (!accepted) return;
            }
          }
          let response;
          if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
            response = await firstValueFrom(this.conceptInvoicingService.updatePurchaseConcept(this.conceptInvoicingContract));
          } else {
            response = await firstValueFrom(this.conceptInvoicingService.updateConceptInvoicing(this.conceptInvoicingContract));
          }
          this.conceptInvoicingContract = response;
          this.validateForm(this.conceptInvoicingContract);
          const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
          this.toastHandlerService.showToast('success', `El concepto de ${message} fue modificado exitosamente.`);
        } catch (error) {
          const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta ? 'Facturación' : 'Compra';
          console.error(`Error al modificar el concepto de ${message}.`, error);
        }
      }
      const output$ = this.config.data?.output$;
      output$?.next({ type: 'reload' });
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.'
      },
      alterCode: {
        maxlength: 'El Código Alterno 1 no puede tener más de 10 caracteres.'
      },
      alterCode2: {
        maxlength: 'El Código Alterno 2 no puede tener más de 10 caracteres.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 70 caracteres.'
      },
      cxcConcept: {
        maxlength: 'El Concepto CxC no puede tener más de 10 caracteres.'
      },
      conceptInvoicingPriceOfId: {
        required: 'Debe seleccionar un Precio de Facturación.',
        min: 'Debe seleccionar un Precio de Facturación válido.'
      },
      affectsInventory: {
        required: 'Debe indicar si afecta el inventario.'
      },
      promptPayment: {
        required: 'Debe indicar si aplica pronto pago.'
      },
      typeConcept: {
        required: 'Debe indicar el tipo de concepto.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formConceptInvoicing.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => {
        this.formConceptInvoicing.enable();
        const control = this.formConceptInvoicing.controls['code'];
        this.conceptInvoicingId > 0 ? control.enable() : control.disable();
      });
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formConceptInvoicing.disable());
    }
  }

  updateInput(conceptInvoicingContract: ConceptInvoicingModel) {
    this.inputsForAssignFamilies$.next({
      conceptInvoicingId: conceptInvoicingContract.conceptInvoicingId,
    });
    this.assignFamiliesModalComponent && this.assignFamiliesModalComponent.show();
  }

  validateForm(conceptInvoicing: ConceptInvoicingModel) {
    const isVenta = this.conceptInvoicingType === ConceptInvoicingTypeEnums.venta;

    this.formConceptInvoicing = this.formBuilder.group({
      code: [conceptInvoicing.code, [Validators.required]],
      alterCode: [conceptInvoicing.alterCode, [Validators.maxLength(10)]],
      alterCode2: [conceptInvoicing.alterCode2, [Validators.maxLength(10)]],
      name: [conceptInvoicing.name, [Validators.required, Validators.maxLength(70)]],
      cxcConcept: [conceptInvoicing.cxcConcept, [Validators.maxLength(10)]],
      affectsInventory: [conceptInvoicing.affectsInventory],
      promptPayment: [conceptInvoicing.promptPayment],
      conceptInvoicingPriceOfId: [conceptInvoicing.conceptInvoicingPriceOfId, isVenta ? [Validators.required, Validators.min(1)] : []],
      swDispersedFamily: [conceptInvoicing.swDispersedFamily],
      typeConcept: [conceptInvoicing.conceptInvoicingType, [Validators.required]]
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formConceptInvoicing.controls).forEach((key) => {
      const control = this.formConceptInvoicing.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}